import React, { useEffect, memo } from 'react';
import { Button } from '../ui/button';
import { ChevronRight, ChevronDown, Users, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserGroups } from '../../hooks/useUserGroups';

interface DynamicGroupMenuProps {
  expandedMenus: Record<string, boolean>;
  toggleMenu: (menuName: string) => void;
  isDesktopMenuCollapsed: boolean;
  isMobileMenuOpen: boolean;
  navigateTo: (path: string) => void;
}

const DynamicGroupMenuComponent: React.FC<DynamicGroupMenuProps> = ({
  expandedMenus,
  toggleMenu,
  isDesktopMenuCollapsed,
  isMobileMenuOpen,
  navigateTo
}) => {
  const { tiposDeGrupos, gruposPorTipo, reloadGroups } = useUserGroups();

  // Recarregar grupos periodicamente para detectar remoções
  useEffect(() => {
    // Aumentar intervalo para 30 segundos (menos invasivo)
    const interval = setInterval(() => {
      // Reload silencioso para não fechar os menus
      reloadGroups(true);
    }, 30000);

    // Listener para mudanças de membership (remoções imediatas)
    const handleMembershipChange = () => {
      console.log('[DynamicGroupMenu] Detectada mudança de membership, recarregando...');
      // Reload silencioso para não fechar os menus
      reloadGroups(true);
    };

    window.addEventListener('grupo-membership-changed', handleMembershipChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('grupo-membership-changed', handleMembershipChange);
    };
  }, [reloadGroups]);

  // Se não há grupos, não renderiza nada (sem loading)
  if (tiposDeGrupos.length === 0) {
    return null;
  }

  return (
    <>
      {tiposDeGrupos.map((tipo) => {
        const tipoMenuKey = `grupo-tipo-${tipo.id}`;
        const gruposDoTipo = gruposPorTipo[tipo.id] || [];

        if (gruposDoTipo.length === 0) return null;

        return (
          <div key={tipo.id}>
            <Button
              variant="ghost"
              className={`w-full hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
                isDesktopMenuCollapsed ? "lg:justify-center lg:px-2" : "justify-between"
              }`}
              style={{
                color: 'oklch(var(--sidebar-foreground) / 0.7)',
                backgroundColor: tipo.cor_identificacao ? `${tipo.cor_identificacao}15` : 'transparent'
              }}
              onClick={() => toggleMenu(tipoMenuKey)}
            >
              <div className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${isDesktopMenuCollapsed ? "mr-3 lg:mr-0" : "mr-3"}`}
                  style={{
                    backgroundColor: tipo.cor_identificacao || 'oklch(var(--sidebar-primary))'
                  }}
                />
                {(!isDesktopMenuCollapsed || isMobileMenuOpen) && (
                  <span className="truncate">{tipo.nome}</span>
                )}
              </div>
              {(!isDesktopMenuCollapsed || isMobileMenuOpen) &&
                (expandedMenus[tipoMenuKey] ? (
                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                ))}
            </Button>

            {expandedMenus[tipoMenuKey] && (!isDesktopMenuCollapsed || isMobileMenuOpen) && (
              <div className="ml-6 mt-2 space-y-1">
                {gruposDoTipo.map((militarGrupo) => {
                  const grupo = militarGrupo.grupo;
                  const cargo = militarGrupo.grupoCargo;

                  return (
                    <Button
                      key={grupo.id}
                      variant="ghost"
                      className="w-full justify-start text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pl-4"
                      style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}
                      onClick={() => navigateTo(`/grupos/${grupo.grupo.toLowerCase().replace(/°/g, '_')}`)}
                    >
                      <div className="flex items-center w-full">
                        <div
                          className="w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0"
                          style={{ backgroundColor: 'oklch(var(--sidebar-foreground) / 0.4)' }}
                        />
                        <div className="flex-1 truncate text-left">
                          <div className="truncate">{grupo.grupo}</div>
                          {cargo && (
                            <div className="text-xs opacity-70 truncate">
                              {cargo.cargo}
                              {cargo.admin && ' (Admin)'}
                              {cargo.responsavel && ' (Resp.)'}
                            </div>
                          )}
                        </div>
                        {militarGrupo.status === 'A' && (
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: '#10b981' }}
                          />
                        )}
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

// Usar memo para evitar re-renders desnecessários quando as props não mudarem
export const DynamicGroupMenu = memo(DynamicGroupMenuComponent);
